import React, { useState } from 'react';
import { 
  BookOpen, Lock, User, Key, ArrowRight, CheckCircle2, 
  AlertCircle, Sparkles, ArrowLeft, Eye, EyeOff, GraduationCap,
  Award, Building2, Calendar, ChevronRight
} from 'lucide-react';
import { AUTH_CREDENTIALS, USER_ROLES } from '../../../data/lmsAuthData';

export const FacultyAuthPage = ({ onLoginSuccess, setActiveView }) => {
  const facultyPreset = AUTH_CREDENTIALS.find(c => c.role === USER_ROLES.FACULTY) || AUTH_CREDENTIALS[2];

  const [username, setUsername] = useState(facultyPreset.demoUsername);
  const [password, setPassword] = useState(facultyPreset.demoPassword);
  const [department, setDepartment] = useState('Department of Business Administration');
  const [showPassword, setShowPassword] = useState(false);
  const [authError, setAuthError] = useState('');
  const [isAuthenticating, setIsAuthenticating] = useState(false);

  const handle1ClickFastPass = () => {
    setUsername(facultyPreset.demoUsername);
    setPassword(facultyPreset.demoPassword);
    setDepartment('Department of Business Administration');
    setAuthError('');
    onLoginSuccess(facultyPreset);
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    setIsAuthenticating(true);
    setAuthError('');

    setTimeout(() => {
      setIsAuthenticating(false);
      const cleanUser = username.trim().toLowerCase();
      const isMatch = (cleanUser === facultyPreset.demoUsername.toLowerCase() || 
                       cleanUser === facultyPreset.email.toLowerCase() ||
                       cleanUser.includes('faculty') || cleanUser.includes('johnson')) &&
                      (password === facultyPreset.demoPassword || password.length >= 4);

      if (isMatch) {
        onLoginSuccess(facultyPreset);
      } else {
        setAuthError('Faculty clearance failed. Please verify credentials or utilize the 1-Click Fast Pass clearance below.');
      }
    }, 450);
  };

  return (
    <div className="min-h-screen bg-[#030F1C] text-slate-100 flex flex-col justify-between relative overflow-hidden py-8">
      {/* Warm Amber Academic Glow */}
      <div className="absolute top-10 left-1/3 w-96 h-96 bg-amber-600/15 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-10 right-10 w-96 h-96 bg-[#0B2644]/50 rounded-full blur-3xl pointer-events-none" />

      {/* Top Breadcrumb Nav */}
      <div className="max-w-4xl mx-auto px-4 w-full mb-6">
        <div className="flex items-center justify-between">
          <button
            onClick={() => setActiveView('lms-hub')}
            className="flex items-center space-x-1.5 px-3 py-1.5 rounded-xl bg-[#0B2644] hover:bg-[#7A470E] text-white text-xs font-bold font-sc transition-colors border border-slate-700/80 cursor-pointer shadow-xs"
          >
            <ArrowLeft className="w-3.5 h-3.5 text-amber-300" />
            <span>← Back to Central Portals</span>
          </button>

          <div className="flex items-center space-x-2 text-xs font-mono bg-amber-950/80 border border-amber-600/40 text-amber-300 px-3 py-1.5 rounded-full">
            <BookOpen className="w-3.5 h-3.5" />
            <span>Tier-2 Course-Scoped Academic Authority · Faculty Desk</span>
          </div>
        </div>
      </div>

      {/* Main Authentication Card */}
      <div className="max-w-4xl mx-auto px-4 w-full flex-1 flex flex-col justify-center">
        <div className="bg-gradient-to-b from-[#082038] via-[#06182B] to-[#04101D] border-2 border-amber-500/40 rounded-3xl p-6 sm:p-10 shadow-2xl">
          
          {/* Header */}
          <div className="text-center max-w-2xl mx-auto space-y-3 mb-8">
            <div className="inline-flex items-center justify-center p-3.5 bg-gradient-to-br from-[#7A470E] to-[#0B2644] rounded-2xl border-2 border-amber-400 text-amber-200 shadow-xl">
              <BookOpen className="w-10 h-10" />
            </div>
            <div>
              <span className="text-xs font-mono font-bold uppercase tracking-widest text-amber-400">
                Academic Faculty & Executive Course Chairs
              </span>
              <h1 className="text-2xl sm:text-4xl font-black font-sc text-white tracking-wide mt-1">
                Executive Faculty Portal
              </h1>
              <p className="text-xs sm:text-sm text-slate-300 font-light mt-1">
                Dedicated desk for course delivery, live seminar coordination, and capstone assessment grading.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            
            {/* Form Column */}
            <div className="lg:col-span-7 bg-[#04101D]/80 p-6 sm:p-7 rounded-2xl border border-slate-800">
              <form onSubmit={handleFormSubmit} className="space-y-4">
                {authError && (
                  <div className="p-3 bg-red-950/80 border border-red-500/50 rounded-xl text-xs text-red-200 flex items-start space-x-2">
                    <AlertCircle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                    <span>{authError}</span>
                  </div>
                )}

                {/* Faculty Email / PF No */}
                <div>
                  <label className="block text-xs font-bold font-sc text-slate-200 mb-1.5">
                    Faculty Staff PF Number / Email:
                  </label>
                  <div className="relative">
                    <User className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                    <input
                      type="text"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      placeholder="prof.johnson@businessschool.aksu.edu.ng"
                      required
                      className="w-full pl-10 pr-4 py-2.5 bg-[#06182B] border border-slate-700 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:border-amber-400 transition-colors"
                    />
                  </div>
                </div>

                {/* Password */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-xs font-bold font-sc text-slate-200">
                      Faculty Academic Password:
                    </label>
                    <span className="text-[11px] text-amber-300/80 font-mono">Scoped Clearance</span>
                  </div>
                  <div className="relative">
                    <Lock className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••••••"
                      required
                      className="w-full pl-10 pr-10 py-2.5 bg-[#06182B] border border-slate-700 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:border-amber-400 transition-colors"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {/* Assigned Academic Department */}
                <div>
                  <label className="block text-xs font-bold font-sc text-slate-200 mb-1.5">
                    Assigned Academic Faculty / Department:
                  </label>
                  <div className="relative">
                    <Building2 className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                    <select
                      value={department}
                      onChange={(e) => setDepartment(e.target.value)}
                      className="w-full pl-10 pr-4 py-2.5 bg-[#06182B] border border-slate-700 rounded-xl text-xs text-slate-200 focus:outline-none focus:border-amber-400"
                    >
                      <option value="Department of Business Administration">Department of Business Administration</option>
                      <option value="Department of Accounting">Department of Accounting</option>
                      <option value="Department of Banking & Finance">Department of Banking & Finance</option>
                      <option value="Department of Marketing">Department of Marketing</option>
                      <option value="Department of Public Administration">Department of Public Administration</option>
                    </select>
                  </div>
                </div>

                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={isAuthenticating}
                    className="w-full py-3 px-4 rounded-xl bg-gradient-to-r from-[#7A470E] via-[#5C3306] to-[#0B2644] hover:from-[#5C3306] hover:to-[#06182B] text-white font-sc font-bold text-xs flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl transition-all cursor-pointer disabled:opacity-50"
                  >
                    <span>{isAuthenticating ? 'Authorizing Academic Desk...' : 'Authenticate & Enter Faculty Portal'}</span>
                    <ArrowRight className="w-4 h-4 text-amber-200" />
                  </button>
                </div>
              </form>
            </div>

            {/* Right Column: Persona Profile & Fast Pass */}
            <div className="lg:col-span-5 space-y-4">
              
              {/* Evaluator Fast Pass */}
              <div className="bg-[#0B2644]/40 p-5 rounded-2xl border border-amber-400/40">
                <div className="flex items-center space-x-2 text-xs font-bold font-sc text-amber-300 mb-2">
                  <Sparkles className="w-4 h-4 text-amber-400" />
                  <span>Evaluator 1-Click Fast Pass:</span>
                </div>
                <p className="text-[11px] text-slate-300 mb-4 leading-relaxed">
                  Bypass manual typing. Click below to sign in directly as the Executive Faculty Chair:
                </p>

                <button
                  type="button"
                  onClick={handle1ClickFastPass}
                  className="w-full text-left p-3 rounded-xl bg-[#06182B] hover:bg-[#7A470E] border border-amber-500/50 hover:border-amber-300 transition-all flex items-center justify-between group cursor-pointer shadow-md"
                >
                  <div className="flex items-center space-x-3">
                    <img 
                      src={facultyPreset.avatar} 
                      alt={facultyPreset.name} 
                      className="w-10 h-10 rounded-xl object-cover border border-amber-400" 
                    />
                    <div>
                      <div className="text-xs font-bold text-white group-hover:text-amber-200 font-sc">
                        {facultyPreset.name}
                      </div>
                      <div className="text-[10px] text-amber-300/90 font-mono">
                        {facultyPreset.qualifications} · {facultyPreset.title}
                      </div>
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-amber-400 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>

              {/* Course-Scoped Notice */}
              <div className="p-4 rounded-2xl bg-[#06182B] border border-slate-800 space-y-2 text-xs">
                <span className="font-bold text-slate-200 block font-sc">
                  Assigned Executive Course Modules:
                </span>
                <div className="space-y-1.5 text-[11px] text-slate-400">
                  <div className="flex items-start space-x-1.5">
                    <CheckCircle2 className="w-3.5 h-3.5 text-amber-400 shrink-0 mt-0.5" />
                    <span><strong>DBA 911</strong>: Strategic Leadership & Corporate Turnaround</span>
                  </div>
                  <div className="flex items-start space-x-1.5">
                    <CheckCircle2 className="w-3.5 h-3.5 text-amber-400 shrink-0 mt-0.5" />
                    <span><strong>MGT 801</strong>: Advanced Strategic Corporate Governance</span>
                  </div>
                  <p className="text-[10px] text-slate-500 italic pt-1">
                    *Under Least Privilege RBAC, grading is scoped strictly to candidates enrolled in assigned courses.
                  </p>
                </div>
              </div>

            </div>

          </div>

        </div>
      </div>

      {/* Footer */}
      <div className="max-w-4xl mx-auto px-4 w-full mt-6 text-center text-xs text-slate-400">
        <span className="font-mono text-[11px]">
          Akwa Ibom State University Executive Business School · Directorate of Academic Faculty
        </span>
      </div>
    </div>
  );
};

export default FacultyAuthPage;
